import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { useImageUpload } from '../hooks/useImageUpload';
import { AppIconButton } from './ui';

interface MenuItemImageUploadProps {
  vendorId: number;
  type: string;
  entityId: number | string;
  imageUrl?: string | null;
  imageKey?: string | null;
  onImageUploaded: (key: string) => void;
  onImageRemoved: () => void;
}

const MenuItemImageUpload: React.FC<MenuItemImageUploadProps> = ({
  vendorId,
  type,
  entityId,
  imageUrl,
  imageKey,
  onImageUploaded,
  onImageRemoved,
}) => {
  const { isUploading, progress, error, uploadImage, deleteImage } = useImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    const key = await uploadImage(file, vendorId, type, entityId);
    if (key) {
      onImageUploaded(key);
      setPreview(null);
    } else {
      setPreview(null);
    }
  }, [uploadImage, vendorId, type, entityId, onImageUploaded]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so re-selecting the same file triggers change
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleFile]);

  const handleRemove = useCallback(async () => {
    if (imageKey) {
      await deleteImage(imageKey);
    }
    onImageRemoved();
  }, [imageKey, deleteImage, onImageRemoved]);

  // Currently has an image
  if (imageUrl && !isUploading && !preview) {
    return (
      <Box
        className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
        sx={{ width: '100%', height: 160 }}
      >
        <img
          src={imageUrl}
          alt="Menu item"
          className="w-full h-full object-cover"
        />
        <Box
          className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <AppIconButton
            icon={<Delete />}
            tooltip="Remove image"
            variant="delete"
            onClick={handleRemove}
            sx={{ color: 'white', backgroundColor: 'rgba(239, 68, 68, 0.8)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 1)' } }}
          />
        </Box>
      </Box>
    );
  }

  // Uploading state
  if (isUploading || preview) {
    return (
      <Box
        className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        sx={{ width: '100%', height: 160 }}
      >
        {preview && (
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
            style={{ opacity: 0.6 }}
          />
        )}
        <Box className="absolute inset-0 flex flex-col items-center justify-center" sx={{ position: 'relative', mt: preview ? -20 : 0 }}>
          <Box sx={{ width: '80%', mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #f97316, #fb923c)',
                },
              }}
            />
          </Box>
          <Typography variant="caption" className="text-gray-500 dark:text-gray-400" sx={{ mt: 0.5 }}>
            {progress < 40 ? 'Compressing...' : progress < 90 ? 'Uploading...' : 'Finishing...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Drop zone (default state)
  return (
    <Box>
      <Box
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`rounded-lg border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center ${
          dragOver
            ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-orange-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
        sx={{ width: '100%', height: 120, userSelect: 'none' }}
      >
        <CloudUpload sx={{ fontSize: 32, color: dragOver ? '#f97316' : '#9ca3af', mb: 0.5 }} />
        <Typography variant="body2" className="text-gray-500 dark:text-gray-400 text-center" sx={{ fontSize: '0.8rem' }}>
          {dragOver ? 'Drop image here' : 'Drag & drop or click to upload'}
        </Typography>
        <Typography variant="caption" className="text-gray-400 dark:text-gray-500" sx={{ fontSize: '0.7rem' }}>
          JPG, PNG, WebP - max 5MB
        </Typography>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default MenuItemImageUpload;
